import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { format } from "date-fns";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import Svg, { Path, Text as SvgText } from "react-native-svg";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Header } from "../../shared/ui/Header";
import ApprovalConfirmModal from "../../shared/utils/ApprovalConfirmModal";
import { Flash } from "../../shared/utils/Flash";
import { useAuth } from "../../../context/AuthContext";
import {
  AdminDevotee,
  AdminService,
  AdminStatData,
  AdminStatType,
} from "../../services/AdminService";
import { UserRegistrationService } from "../../services/UserRegistrationService";

type AdminTab = AdminStatType;

type RegistrationStatusOption = {
  id: string;
  value: string;
};

type ConfirmAction = "approve" | "reject" | "activate" | "deactivate";

type ConfirmDialogState = {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  action: ConfirmAction;
  devotee: AdminDevotee | null;
  variant: ConfirmAction | "warning";
};

const TABS: Array<{ key: AdminTab; label: string }> = [
  { key: "NewDevotees", label: "New Devotees" },
  { key: "AllDevotees", label: "All Devotees" },
];

const normalizeStatusValue = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const buildFullName = (devotee: AdminDevotee) =>
  [devotee.firstName, devotee.lastName].filter(Boolean).join(" ").trim() || "Devotee";

const formatDisplayDate = (value?: string | null) => {
  if (!value) {
    return "N/A";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }

  return format(parsed, "d-M-yyyy");
};

const getStatusTheme = (status?: string | null) => {
  const normalized = normalizeStatusValue(status);

  if (normalized === "active" || normalized === "approved") {
    return {
      label: "Active",
      badgeBg: "rgba(34,197,94,0.16)",
      badgeText: "#166534",
      buttonBg: "rgba(220,38,38,0.10)",
      buttonText: "#B91C1C",
      buttonBorder: "rgba(220,38,38,0.18)",
      cardBorder: "rgba(34,197,94,0.18)",
    };
  }

  if (normalized === "inactive" || normalized === "rejected" || normalized === "deactivated") {
    return {
      label: "Inactive",
      badgeBg: "rgba(239,68,68,0.14)",
      badgeText: "#B91C1C",
      buttonBg: "#16A34A",
      buttonText: "#FFFFFF",
      buttonBorder: "#16A34A",
      cardBorder: "rgba(239,68,68,0.18)",
    };
  }

  return {
    label: "Pending",
    badgeBg: "rgba(249,115,22,0.14)",
    badgeText: "#C2410C",
    buttonBg: "#16A34A",
    buttonText: "#FFFFFF",
    buttonBorder: "#16A34A",
    cardBorder: "rgba(249,115,22,0.18)",
  };
};

function StatusGauge({ percentage }: { percentage: number }) {
  const safePercentage = Math.max(0, Math.min(100, percentage));
  const radius = 38;
  const circumference = Math.PI * radius;
  const dash = (safePercentage / 100) * circumference;

  return (
    <View style={styles.gaugeWrap}>
      <Svg width={112} height={64} viewBox="0 0 112 64">
        <Path
          d="M 18 56 A 38 38 0 0 1 94 56"
          fill="none"
          stroke="rgba(148,163,184,0.22)"
          strokeWidth={12}
          strokeLinecap="round"
        />
        <Path
          d="M 18 56 A 38 38 0 0 1 94 56"
          fill="none"
          stroke="#16A34A"
          strokeWidth={12}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
        />
        <SvgText
          x="56"
          y="42"
          textAnchor="middle"
          fontSize="13"
          fontWeight="700"
          fill="#166534"
        >
          {`${safePercentage}%`}
        </SvgText>
      </Svg>
    </View>
  );
}

export function AdminConsoleScreen() {
  const navigation = useNavigation<any>();
  const { user } = useAuth();
  const isGuru = user?.userType === "Guru";

  const [activeTab, setActiveTab] = useState<AdminTab>("NewDevotees");
  const [stats, setStats] = useState<AdminStatData | null>(null);
  const [statusOptions, setStatusOptions] = useState<RegistrationStatusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionUserId, setActionUserId] = useState("");
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    visible: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    action: "approve",
    devotee: null,
    variant: "approve",
  });

  const pendingCount = Number(stats?.pendingApprovalCount ?? 0);
  const allDevoteesCount = Number(stats?.allDevoteesCount ?? 0);
  const totalRegistered = Number(stats?.registeredUserCount ?? 0);
  const activeUsersCount = Number(stats?.userStatusOverview?.activeUsersCount ?? 0);
  const deactivatedUsersCount = Number(stats?.userStatusOverview?.deactivatedUsersCount ?? 0);
  const activeRate = Number(stats?.userStatusOverview?.activeRate ?? 0);
  const devotees = Array.isArray(stats?.devotees) ? stats?.devotees : [];

  const approvedStatusId = useMemo(
    () =>
      statusOptions.find((item) => normalizeStatusValue(item.value) === "approved")?.id ?? "",
    [statusOptions]
  );

  const rejectedStatusId = useMemo(
    () =>
      statusOptions.find((item) => normalizeStatusValue(item.value) === "rejected")?.id ?? "",
    [statusOptions]
  );

  const loadStatusOptions = useCallback(async () => {
    const response = await UserRegistrationService.getEnumeration("RegistrationStatus");
    const options = Array.isArray(response?.data) ? response.data : [];

    setStatusOptions(
      options
        .map((item: any) => ({
          id: String(item?.enumerationId ?? item?.id ?? ""),
          value: String(item?.value ?? item?.name ?? ""),
        }))
        .filter((item) => item.id && item.value)
    );
  }, []);

  const loadAdminStats = useCallback(async (type: AdminTab) => {
    const response = await AdminService.getAdminStatData(type);

    if (Number(response?.status) !== 200) {
      throw new Error(response?.message ?? "Unable to load admin data.");
    }

    setStats(response.data ?? {});
  }, []);

  const initializeScreen = useCallback(async () => {
    if (!isGuru) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      await Promise.all([loadStatusOptions(), loadAdminStats("NewDevotees")]);
      setActiveTab("NewDevotees");
    } catch (error: any) {
      Flash.error(error?.message ?? "Unable to load admin console.");
    } finally {
      setLoading(false);
    }
  }, [isGuru, loadAdminStats, loadStatusOptions]);

  useFocusEffect(
    useCallback(() => {
      initializeScreen();
    }, [initializeScreen])
  );

  const handleRefresh = async () => {
    if (!isGuru) {
      return;
    }

    try {
      setRefreshing(true);
      await loadAdminStats(activeTab);
    } catch (error: any) {
      Flash.error(error?.message ?? "Unable to refresh admin console.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTabChange = async (type: AdminTab) => {
    if (type === activeTab) {
      return;
    }

    try {
      setLoading(true);
      setActiveTab(type);
      await loadAdminStats(type);
    } catch (error: any) {
      Flash.error(error?.message ?? "Unable to switch data.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    devotee: AdminDevotee,
    action: ConfirmAction
  ) => {
    const isPositiveAction = action === "approve" || action === "activate";
    const nextStatusId = isPositiveAction ? approvedStatusId : rejectedStatusId;

    if (!nextStatusId) {
      Flash.error("Registration status mapping was not found.");
      return false;
    }

    try {
      setActionUserId(devotee.userId);
      const response = await AdminService.updateUser({
        userId: devotee.userId,
        registrationStatus: nextStatusId,
      });

      if (Number(response?.status) !== 200) {
        Flash.error(response?.message ?? "Unable to update devotee.");
        return false;
      }

      Flash.success(
        "Success!",
        action === "approve"
          ? "Devotee approved successfully."
          : action === "reject"
            ? "Devotee rejected successfully."
            : action === "activate"
              ? "Devotee activated successfully."
              : "Devotee deactivated successfully."
      );

      await loadAdminStats(activeTab);
      return true;
    } catch (error: any) {
      Flash.error(error?.message ?? "Unable to update devotee.");
      return false;
    } finally {
      setActionUserId("");
    }
  };

  const openConfirmDialog = (
    devotee: AdminDevotee,
    config: Omit<ConfirmDialogState, "visible" | "devotee">
  ) => {
    setConfirmDialog({
      ...config,
      visible: true,
      devotee,
    });
  };

  const closeConfirmDialog = () => {
    if (confirmDialog.devotee && actionUserId === confirmDialog.devotee.userId) {
      return;
    }

    setConfirmDialog((current) => ({
      ...current,
      visible: false,
      devotee: null,
    }));
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.devotee) {
      return;
    }

    const isSuccessful = await handleStatusUpdate(
      confirmDialog.devotee,
      confirmDialog.action
    );

    if (isSuccessful) {
      setConfirmDialog((current) => ({
        ...current,
        visible: false,
        devotee: null,
      }));
    }
  };

  const handleApproveClick = (devotee: AdminDevotee) => {
    if (devotee.isActive === false) {
      openConfirmDialog(devotee, {
        title: "Approve Inactive Devotee?",
        message:
          "Hare Krishna. This devotee is currently marked inactive. Approving now will still grant access. Would you like to continue?",
        confirmLabel: "Approve",
        action: "approve",
        variant: "warning",
      });
      return;
    }

    openConfirmDialog(devotee, {
      title: "Bless This Approval",
      message: `Hare Krishna. Approve ${buildFullName(devotee)} and welcome them into the app experience?`,
      confirmLabel: "Approve",
      action: "approve",
      variant: "approve",
    });
  };

  const handleRejectClick = (devotee: AdminDevotee) => {
    openConfirmDialog(devotee, {
      title: "Reject Request?",
      message: `Hare Krishna. Are you sure you want to reject ${buildFullName(devotee)} at this stage?`,
      confirmLabel: "Reject",
      action: "reject",
      variant: "reject",
    });
  };

  const handleTrackDevotee = (devotee: AdminDevotee) => {
    navigation.navigate("SadhanaTracker", {
      initialTab: "track",
      selectedDevotee: {
        userId: devotee.userId,
        name: buildFullName(devotee),
      },
    });
  };

  if (!isGuru) {
    return (
      <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.92}>
        <Header title="Admin Console" showBack showProfile />
        <View style={styles.deniedWrap}>
          <View style={styles.deniedCard}>
            <MaterialCommunityIcons name="shield-lock-outline" size={40} color="#C2410C" />
            <Text style={styles.deniedTitle}>Access Restricted</Text>
            <Text style={styles.deniedText}>
              This screen is available only for Guru users.
            </Text>
          </View>
        </View>
      </DivineBackground>
    );
  }

  return (
    <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.9}>
      <Header title="Admin Console" showBack showProfile />

      <ScrollView
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={["#F97316"]}
            tintColor="#F97316"
          />
        }
      >
        <View style={styles.summaryRow}>
          <View style={[styles.summaryCard, styles.summaryCardBlue]}>
            <View style={styles.summaryTop}>
              <MaterialCommunityIcons name="account-group-outline" size={18} color="#2563EB" />
              <Text style={styles.summaryLabel}>Total Registered</Text>
            </View>
            <Text style={[styles.summaryValue, { color: "#1D4ED8" }]}>{totalRegistered}</Text>
          </View>

          <View style={[styles.summaryCard, styles.summaryCardOrange]}>
            <View style={styles.summaryTop}>
              <MaterialCommunityIcons name="clock-outline" size={18} color="#EA580C" />
              <Text style={styles.summaryLabel}>Approval Pending</Text>
            </View>
            <Text style={[styles.summaryValue, { color: "#C2410C" }]}>{pendingCount}</Text>
          </View>
        </View>

        <View style={styles.overviewCard}>
          <View style={styles.overviewLeft}>
            <View style={styles.overviewHeader}>
              <MaterialCommunityIcons name="chart-arc" size={18} color="#166534" />
              <Text style={styles.overviewTitle}>User Status Overview</Text>
            </View>

            <View style={styles.overviewRow}>
              <Text style={styles.overviewLabel}>Active Users</Text>
              <Text style={[styles.overviewValue, { color: "#166534" }]}>{activeUsersCount}</Text>
            </View>
            <View style={styles.overviewRow}>
              <Text style={styles.overviewLabel}>Deactivated</Text>
              <Text style={[styles.overviewValue, { color: "#B91C1C" }]}>{deactivatedUsersCount}</Text>
            </View>
            <View style={styles.overviewRow}>
              <Text style={styles.overviewLabel}>Active Rate</Text>
              <Text style={[styles.overviewValue, { color: "#2563EB" }]}>{activeRate}%</Text>
            </View>
          </View>

          <StatusGauge percentage={activeRate} />
        </View>

        <View style={styles.tabBar}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const count = tab.key === "NewDevotees" ? pendingCount : allDevoteesCount;

            return (
              <Pressable
                key={tab.key}
                onPress={() => handleTabChange(tab.key)}
                style={[
                  styles.tabButton,
                  isActive
                    ? tab.key === "NewDevotees"
                      ? styles.pendingTabActive
                      : styles.allTabActive
                    : null,
                ]}
              >
                <Text style={[styles.tabText, isActive ? styles.tabTextActive : null]}>
                  {`${tab.label} (${count})`}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {loading ? (
          <View style={styles.loadingCard}>
            <ActivityIndicator color="#F97316" size="large" />
            <Text style={styles.loadingText}>Loading admin data...</Text>
          </View>
        ) : devotees.length === 0 ? (
          <View style={styles.emptyCard}>
            <MaterialCommunityIcons
              name={activeTab === "NewDevotees" ? "account-clock-outline" : "account-group-outline"}
              size={46}
              color="#94A3B8"
            />
            <Text style={styles.emptyTitle}>
              {activeTab === "NewDevotees" ? "No pending approvals" : "No devotees found"}
            </Text>
          </View>
        ) : (
          devotees.map((devotee) => {
            const theme = getStatusTheme(devotee.status);
            const isPendingTab = activeTab === "NewDevotees";
            const isBusy = actionUserId === devotee.userId;

            return (
              <View
                key={devotee.userId}
                style={[styles.devoteeCard, { borderColor: theme.cardBorder }]}
              >
                <View style={styles.devoteeHeader}>
                  <View style={styles.devoteeInfo}>
                    <Text style={styles.devoteeName}>{buildFullName(devotee)}</Text>
                    <Text style={styles.devoteeMeta}>{devotee.mobileNumber || "No mobile number"}</Text>
                    {devotee.email ? <Text style={styles.devoteeMeta}>{devotee.email}</Text> : null}

                    <View style={styles.dateRow}>
                      <Text style={styles.dateMeta}>
                        {`Role: ${devotee.userType ?? "N/A"} | ${
                          isPendingTab ? "Registered" : "Approved"
                        }: ${
                          isPendingTab
                            ? formatDisplayDate(devotee.registrationDate)
                            : formatDisplayDate(devotee.approvedDate ?? devotee.approvedDateTime)
                        }`}
                      </Text>

                      <Text style={styles.verifyMeta}>
                        {`Email: ${
                          devotee.isEmailVerified ? "Verified" : "Not Verified"
                        } | Mobile: ${
                          devotee.isMobileVerified ? "Verified" : "Not Verified"
                        }`}
                      </Text>
                    </View>
                  </View>

                  <View style={[styles.statusBadge, { backgroundColor: theme.badgeBg }]}>
                    <Text style={[styles.statusBadgeText, { color: theme.badgeText }]}>
                      {theme.label}
                    </Text>
                  </View>
                </View>

                {isPendingTab ? (
                  <View style={styles.pendingActions}>
                    <Pressable
                      onPress={() => handleApproveClick(devotee)}
                      disabled={isBusy}
                      style={[
                        styles.actionButton,
                        styles.primaryAction,
                        isBusy ? styles.disabledAction : null,
                      ]}
                    >
                      <MaterialCommunityIcons name="check-circle-outline" size={16} color="#FFFFFF" />
                      <Text style={styles.primaryActionText}>
                        {isBusy ? "Processing..." : "Approve"}
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={() => handleRejectClick(devotee)}
                      disabled={isBusy}
                      style={[
                        styles.actionButton,
                        styles.rejectAction,
                        isBusy ? styles.disabledAction : null,
                      ]}
                    >
                      <MaterialCommunityIcons name="close-circle-outline" size={16} color="#B91C1C" />
                      <Text style={styles.rejectActionText}>Reject</Text>
                    </Pressable>
                  </View>
                ) : (
                  <View style={styles.allDevoteeActions}>
                    <Pressable
                      onPress={() =>
                        openConfirmDialog(devotee, {
                          title:
                            theme.label === "Active"
                              ? "Deactivate Devotee?"
                              : "Activate Devotee?",
                          message:
                            theme.label === "Active"
                              ? `Hare Krishna. ${buildFullName(devotee)} will lose active access until enabled again. Do you want to continue?`
                              : `Hare Krishna. Reactivate ${buildFullName(devotee)} so they can access the app again?`,
                          confirmLabel:
                            theme.label === "Active" ? "Deactivate" : "Activate",
                          action: theme.label === "Active" ? "deactivate" : "activate",
                          variant: theme.label === "Active" ? "deactivate" : "activate",
                        })
                      }
                      disabled={isBusy}
                      style={[
                        styles.singleActionButton,
                        {
                          backgroundColor: theme.buttonBg,
                          borderColor: theme.buttonBorder,
                        },
                        isBusy ? styles.disabledAction : null,
                      ]}
                    >
                      <MaterialCommunityIcons
                        name={theme.label === "Active" ? "account-cancel-outline" : "check-circle-outline"}
                        size={16}
                        color={theme.buttonText}
                      />
                      <Text style={[styles.singleActionText, { color: theme.buttonText }]}>
                        {isBusy
                          ? "Processing..."
                          : theme.label === "Active"
                            ? "Deactivate"
                            : "Activate"}
                      </Text>
                    </Pressable>

                    {theme.label === "Active" ? (
                      <Pressable
                        onPress={() => handleTrackDevotee(devotee)}
                        disabled={isBusy}
                        style={[
                          styles.singleActionButton,
                          styles.trackActionButton,
                          isBusy ? styles.disabledAction : null,
                        ]}
                      >
                        <MaterialCommunityIcons
                          name="heart-pulse"
                          size={16}
                          color="#FFFFFF"
                        />
                        <Text style={styles.trackActionText}>Track Devotee</Text>
                      </Pressable>
                    ) : null}
                  </View>
                )}
              </View>
            );
          })
        )}
      </ScrollView>

      <ApprovalConfirmModal
        visible={confirmDialog.visible}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
        loading={
          !!confirmDialog.devotee && actionUserId === confirmDialog.devotee.userId
        }
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmAction}
      />
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 18,
    gap: 14,
  },
  deniedWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  deniedCard: {
    width: "100%",
    maxWidth: 360,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.24)",
    padding: 24,
    alignItems: "center",
    gap: 10,
  },
  deniedTitle: {
    fontSize: 18,
    fontWeight: "900",
    color: "#7C2D12",
  },
  deniedText: {
    fontSize: 13,
    fontWeight: "600",
    textAlign: "center",
    color: "rgba(74,35,17,0.70)",
    lineHeight: 20,
  },
  summaryRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "stretch",
  },
  summaryCard: {
    flex: 1,
    borderRadius: 18,
    minHeight: 112,
    paddingHorizontal: 11,
    paddingVertical: 16,
    borderWidth: 1,
    backgroundColor: "rgba(245, 244, 244, 0.92)",
    // elevation: 1,
    // justifyContent: "space-between",
  },
  summaryCardBlue: {
    borderColor: "rgba(107, 153, 225, 0.18)",
  },
  summaryCardOrange: {
    borderColor: "rgba(234, 153, 95, 0.18)",
  },
  summaryTop: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 7,
    marginBottom: 14,
  },
  summaryLabel: {
    flex: 1,
    flexShrink: 1,
    fontSize: 13,
    fontWeight: "800",
    lineHeight: 18,
    color: "rgba(71,85,105,0.88)",
  },
  summaryValue: {
    fontSize: 30,
    fontWeight: "900",
    lineHeight: 45,
  },
  overviewCard: {
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "rgba(34,197,94,0.18)",
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  overviewLeft: {
    flex: 1,
    gap: 10,
  },
  overviewHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  overviewTitle: {
    fontSize: 14,
    fontWeight: "800",
    color: "#1F2937",
  },
  overviewRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  overviewLabel: {
    fontSize: 13,
    color: "rgba(71,85,105,0.88)",
    fontWeight: "600",
  },
  overviewValue: {
    fontSize: 13,
    fontWeight: "900",
  },
  gaugeWrap: {
    width: 112,
    alignItems: "center",
    justifyContent: "center",
  },
  tabBar: {
    flexDirection: "row",
    gap: 10,
    backgroundColor: "rgba(255,255,255,0.78)",
    borderRadius: 18,
    padding: 6,
    borderWidth: 1,
    borderColor: "rgba(226,232,240,0.92)",
  },
  tabButton: {
    flex: 1,
    minHeight: 40,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  pendingTabActive: {
    backgroundColor: "#F97316",
  },
  allTabActive: {
    backgroundColor: "#16A34A",
  },
  tabText: {
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(71,85,105,0.9)",
  },
  tabTextActive: {
    color: "#FFFFFF",
  },
  loadingCard: {
    minHeight: 220,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    backgroundColor: "rgba(255,255,255,0.86)",
    borderWidth: 1,
    borderColor: "rgba(226,232,240,0.92)",
  },
  loadingText: {
    fontSize: 13,
    fontWeight: "700",
    color: "rgba(71,85,105,0.88)",
  },
  emptyCard: {
    minHeight: 220,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "rgba(255,255,255,0.86)",
    borderWidth: 1,
    borderColor: "rgba(226,232,240,0.92)",
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: "800",
    color: "rgba(71,85,105,0.88)",
  },
  devoteeCard: {
    backgroundColor: "rgba(255,255,255,0.92)",
    borderWidth: 1,
    borderRadius: 18,
    padding: 14,
    gap: 14,
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 7 },
    elevation: 3,
  },
  devoteeHeader: {
    flexDirection: "row",
    gap: 10,
    justifyContent: "space-between",
  },
  devoteeInfo: {
    flex: 1,
    gap: 3,
  },
  devoteeName: {
    fontSize: 15,
    fontWeight: "900",
    color: "#1F2937",
  },
  devoteeMeta: {
    fontSize: 12,
    fontWeight: "600",
    color: "rgba(71,85,105,0.86)",
  },
  dateRow: {
    marginTop: 2,
  },
  dateMeta: {
    fontSize: 11,
    fontWeight: "700",
    color: "rgba(100,116,139,0.92)",
  },
  verifyMeta: {
    fontSize: 11,
    fontWeight: "700",
    color: "#475569",
    marginTop: 2,
  },
  statusBadge: {
    alignSelf: "flex-start",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "900",
  },
  pendingActions: {
    flexDirection: "row",
    gap: 10,
  },
  actionButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 6,
    borderWidth: 1,
  },
  primaryAction: {
    backgroundColor: "#16A34A",
    borderColor: "#16A34A",
  },
  primaryActionText: {
    color: "#FFFFFF",
    fontWeight: "800",
    fontSize: 12,
  },
  rejectAction: {
    backgroundColor: "rgba(254,242,242,0.95)",
    borderColor: "rgba(239,68,68,0.22)",
  },
  rejectActionText: {
    color: "#B91C1C",
    fontWeight: "800",
    fontSize: 12,
  },
  singleActionButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 6,
    borderWidth: 1,
  },
  singleActionText: {
    fontWeight: "800",
    fontSize: 12,
  },
  allDevoteeActions: {
    flexDirection: "row",
    gap: 10,
  },
  trackActionButton: {
    backgroundColor: "#16A34A",
    borderColor: "#16A34A",
  },
  trackActionText: {
    color: "#FFFFFF",
    fontWeight: "800",
    fontSize: 12,
  },
  disabledAction: {
    opacity: 0.6,
  },
});
